import {
    Button,
    Flex,
    keyCodes,
    PriorityIcon,
    Separator,
    Title,
    Text,
    AIStarIcon
} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import cx from 'classnames';
import React, {useCallback, useEffect} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {ButtonWithTooltip} from '../../../_legacy/FormHeader/components/UniqueScreensHeader';
import {runReport} from '../../../_shared/GWTBridge/invoke/genPage';
import helpTexts from '../../../_shared/Help/texts';
import {
    showConfirmMessage,
    showHelpMessage,
    showWarnMessage
} from '../../../_shared/Messages/actions/MessageActions';
import {saveReport} from '../services/service';
import {monitorTransactionValues, Pages, advancedAnalyticsModuleId} from '../consts';
import {reportGeneratorsStore} from '../stores/ReportGeneratorsStore';
import texts from '../texts';
import styles from './styles.module.scss';
import {sendAnalyticsMonitor} from '../../../utils/monitoring';
import {fieldTableValidation, validateSortValue} from './validations';
import {ScreenLocker} from '../../../features/Form/components/ScreenLocker';
import {createDataTestId} from '../../../utils/dataTestId';
import {setServerStoreData} from '../../../services/proto/ServerStorage/service';
import {Action, getStorageKey, Namespace} from '../../../services/proto/ServerStorage/utils';
import {common, priority} from '@priority-software/priority-proto-api/lib';
import {cancelReport} from '../services/reports';
import {UpdateTimestamp} from '../types';
import {formatDate, isDateByStringStructure, isDate} from '../../../features/Form/utils/date';
import {SingleSeriesChartTypesOptions} from '../chartConsts';
import {isRTL} from '../../../Localization';
import {utf8ToBase64} from '../../../utils/base64';
import {checkLicense} from '../../License/services/licenses';

interface Props {
    onClose: () => void;
}

const HeaderInputs = ({inputs}: {inputs: priority.netitems.nrest.IDataInputInfo[]}) => {
    const isRtl = isRTL();
    return (
        <Flex justifyContent="flex-start">
            {inputs.map((input, index) => {
                const isLast = index === inputs.length - 1;
                const {Title, Sign, Value} = input;

                return (
                    <Flex
                        key={`${Title}-${index}`}
                        justifyContent="flex-start"
                        componentClasses={styles.inputWrapper}
                    >
                        <Text>{Title}</Text>
                        <Text componentClasses={styles.inputSign}>{Sign}</Text>
                        <Text
                            componentClasses={cx({
                                [styles.forceLtr]: isRtl && isDateByStringStructure(Value)
                            })}
                        >
                            {Value}
                        </Text>
                        {!isLast && <Text componentClasses={styles.commaSeparator}>,</Text>}
                    </Flex>
                );
            })}
        </Flex>
    );
};

const Header = observer(({onClose}: Props) => {
    const {currentScreen} = reportGeneratorsStore;
    const {
        currentPage,
        currentReport,
        session,
        setCurrentPage,
        setPrevPosition,
        prevPosition,
        getPageTitle,
        chartIsReady,
        chartInfo,
        setChartReady,
        checkAxesCompleted,
        chartHeader,
        isPredictiveModel
    } = currentScreen;
    const isMain = currentPage === Pages.main;
    const isReport = currentPage === Pages.report;
    const isChart = currentPage === Pages.chart;
    const isChartDisplay = currentPage === Pages.chartDisplay;

    const title = getPageTitle();

    const queryClient = useQueryClient();

    const handleSaveReport = async () => {
        const errorMessage = fieldTableValidation(currentReport);
        if (!errorMessage) {
            if (isChart) {
                currentReport.setUseAsChart();
            } else {
                validateSortValue(currentReport);
            }
            currentReport.resetDirty();

            return await saveReport(currentReport);
        } else {
            throw new Error(errorMessage);
        }
    };
    const {isLoading, mutate: onSaveReport} = useMutation(handleSaveReport, {
        onSuccess: reportId => {
            currentReport.setReportId(reportId);

            queryClient.invalidateQueries(['currentReport', reportId, session]);

            !!currentReport.duplicateId && currentReport.setDuplicate(null);
            if (currentReport.outerJoinMap) {
                const mapKey = getStorageKey({
                    nameSpace: Namespace.Analytics,
                    action: Action.onOuterJoinSave,
                    storage: reportId.toString()
                });
                const encodedMap = utf8ToBase64(JSON.stringify(currentReport.outerJoinMap));
                const storeValue = {[mapKey]: new common.Constant({Str: encodedMap})};
                setServerStoreData(storeValue, false, false);
            }
            if (isChart) {
                const key = getStorageKey({
                    nameSpace: Namespace.Analytics,
                    action: Action.onChartSave,
                    storage: reportId.toString()
                });
                const storeMap = {[key]: new common.Constant({Str: JSON.stringify(chartInfo)})};

                setServerStoreData(storeMap, false, false);
                setChartReady(true);
            }
            sendAnalyticsMonitor(
                monitorTransactionValues.AnalyticsSetSave,
                'success',
                currentReport.title
            );
            if (currentReport.isUseAsChart) {
                const {type, explanatoryReport} = currentScreen?.chartInfo;
                const chartSeriesType = SingleSeriesChartTypesOptions[type]
                    ? 'single-chart'
                    : 'multi-chart';
                const isExplainReport = !!explanatoryReport ? 'Y' : 'N';
                type &&
                    sendAnalyticsMonitor(
                        monitorTransactionValues.AnalyticsChartType,
                        chartSeriesType
                    );
                sendAnalyticsMonitor(
                    monitorTransactionValues.AnalyticsExplainReport,
                    isExplainReport
                );
                sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSaveChart);
            }
        },
        onError: errorMessage => {
            showWarnMessage({
                content: (errorMessage as Error)?.message
            });
        }
    });

    const handleCloseSettingPage = () => {
        setPrevPosition(Pages.main);
        setCurrentPage(Pages.main);

        const {currentScreen} = reportGeneratorsStore;
        const {currentReport} = currentScreen;

        if (isChart) {
            currentScreen.setCurrentChartTab('0');
        }

        if (currentReport?.linkId) {
            cancelReport(currentReport?.linkId);
        }
        queryClient.invalidateQueries(['reports', session]);
    };

    const handleConfirmation = () => {
        handleCloseSettingPage();

        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSetChangesYes);
    };

    const handleClose = () => {
        if (isMain) {
            onClose();
            sendAnalyticsMonitor(monitorTransactionValues.AnalyticsMainX);
        } else if (isChartDisplay) {
            setCurrentPage(prevPosition);
        } else {
            if (currentReport?.isDirty) {
                showConfirmMessage({
                    title: texts.exitNoSave,
                    confirmButtonText: texts.yes,
                    cancleButtonText: texts.no,
                    defaultButton: 0,
                    onConfirm: handleConfirmation,
                    onCancel: () =>
                        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSetChangesNo)
                });
            } else {
                handleCloseSettingPage();
                sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSetX);
            }
        }
    };

    const handleRunReport = useCallback(async () => {
        const reportType = isChart ? priority.netitems.nrest.RepType.RepType_Chart : null;
        if (isChart || currentReport.isUseAsDataset) {
            await checkLicense(advancedAnalyticsModuleId);
            runReport(currentReport.reportId.toString(), reportType);
        } else {
            runReport(
                currentReport.reportId.toString(),
                priority.netitems.nrest.RepType.RepType_Report
            );
        }
        sendAnalyticsMonitor(
            isChart
                ? monitorTransactionValues.AnalyticsRunChart
                : monitorTransactionValues.AnalyticsSetRun,
            isChart ? currentScreen.chartInfo.type : 'success',
            currentReport.title
        );
    }, [currentPage, currentReport]);

    const onHelp = () => {
        const content = isMain
            ? texts.analyticsHelp
            : isReport
            ? isPredictiveModel
                ? texts.predictiveModelReportHelp
                : texts.analyticsReportHelp
            : texts.analyticsChartHelp;
        showHelpMessage(content);
        sendAnalyticsMonitor(
            isMain
                ? monitorTransactionValues.AnalyticsMainHelp
                : monitorTransactionValues.AnalyticsSetHelp
        );
    };

    const handleKeyDown = e => {
        if (e.keyCode === keyCodes.ESC) {
            if (currentReport?.openFunctionDialog?.isOpen) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            handleClose();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMain, isChartDisplay]);

    const getIsDisabled = useCallback(
        isSaveDisabled => {
            if (isChart) {
                if (!isSaveDisabled) {
                    return true;
                }

                return !chartIsReady;
            } else if (isReport) {
                return !currentReport?.columns?.length || currentReport?.isDirty;
            } else {
                return false;
            }
        },
        [currentPage, currentReport, chartIsReady]
    );

    const isAxesCompleted = useCallback(() => {
        const axesCompleted = checkAxesCompleted(chartInfo);
        if (!axesCompleted) {
            setChartReady(false);
        }
        return axesCompleted;
    }, [chartInfo]);

    const hasRequiredXaxis = useCallback(() => {
        return (
            currentReport?.isTabular &&
            currentReport?.columns?.length > 0 &&
            !currentReport?.columns?.find(col => !!col.AxisMode)
        );
    }, [currentReport]);

    const isMissingRequiredInPredictiveReport = useCallback(() => {
        if (currentReport?.isUseAsDataset) {
            const {columns} = currentReport;
            const dateInputField = columns?.find(col => isDate(col.Type) && col.Input);
            const groupFieldIndex = columns.findIndex(
                col => Number(col.Group) > 0 && !isDate(col.Type)
            );
            const hasGroupField = groupFieldIndex >= 0;
            const hasFuncUnderGroup =
                hasGroupField &&
                !!columns.find((col, idx) => idx > groupFieldIndex && Number(col.Func) > 0);

            return columns?.length > 0 && (!dateInputField || !hasGroupField || !hasFuncUnderGroup);
        }
        return false;
    }, [currentReport]);

    const isSaveDisabled =
        !currentReport?.isDirty ||
        !currentReport?.title?.trim() ||
        !currentReport?.columns?.length ||
        isMissingRequiredInPredictiveReport() || // EP-51182 Validations for Predictive Reports
        !currentReport?.columns?.find(col => col.Input) || // EP-39057: Contains at least one Input Column
        hasRequiredXaxis() ||
        isLoading;

    const isSaveChartButtonDisabled =
        !chartInfo.chartName.trim() || !isAxesCompleted() || isSaveDisabled;

    const shouldShowSaveTooltip = isChart
        ? isSaveChartButtonDisabled && !currentScreen.chartIsReady
        : isSaveDisabled;

    const getSaveButtonTooltip = useCallback(() => {
        const tooltip = [];
        // Verify there is a title
        if (isChart && !chartInfo.chartName.trim()) {
            tooltip.push(texts.missingChartTitleTooltip);
        } else if (!currentReport?.title?.trim()) {
            tooltip.push(texts.missingReportNameTooltip);
        }
        if (currentReport.isUseAsDataset) {
            // Verify compliance with predictive report requirements (EP-51182)
            const dateCols = currentReport.columns.filter(col => isDate(col.Type));
            const dateInputField = dateCols?.find(col => col.Input);
            if (dateCols.length === 0) {
                tooltip.push(texts.missingDateFieldTooltip);
            } else if (!dateInputField) {
                tooltip.push(texts.missingDateFieldInputTooltip);
            }
            const groupFieldIndex = currentReport.columns.findIndex(
                col => Number(col.Group) > 0 && !isDate(col.Type)
            );
            const hasGroupField = groupFieldIndex >= 0;
            if (!hasGroupField) {
                tooltip.push(texts.missingGroupedFieldTooltip);
            }
            const funcFieldIndex = currentReport.columns.findIndex(col => Number(col.Func) > 0);
            const hasFunctionField = funcFieldIndex >= 0;
            if (!hasFunctionField || funcFieldIndex < groupFieldIndex) {
                tooltip.push(texts.missingFunctionFieldTooltip);
            }
        } else {
            // Verify at least one field is selected
            if (!currentReport?.columns?.length) {
                tooltip.push(texts.missingFieldsTooltip);
            } else if (!currentReport?.columns?.find(col => col.Input)) {
                // Verify at least one input field is selected
                tooltip.push(texts.missingInputFieldTooltip);
            }
        }
        // Verify an X axis was selected for pivot table
        if (hasRequiredXaxis()) {
            tooltip.push(texts.missingXaxisTooltip);
        }
        // Verify all required chart dimensions are properly filled
        if (isChart && !isAxesCompleted()) {
            tooltip.push(texts.missingDimensionsTooltip);
        }
        if (tooltip.length > 0) {
            tooltip.unshift(texts.preTextMissingTooltip);
        }
        if (tooltip.length > 2) {
            const checkList = tooltip.map((item, idx) => {
                if (idx === 0) {
                    return `${item}\n`;
                } else {
                    const capitalized = item.charAt(0).toUpperCase() + item.slice(1);
                    return `${idx}. ${capitalized}\n`;
                }
            });
            return <Text skin="paragraph-2">{checkList.join('')}</Text>;
        } else if (!!tooltip.length) {
            return <Text skin="paragraph-2">{tooltip.join(' ')}</Text>;
        } else {
            return '';
        }
    }, [chartInfo, currentReport]);

    const updatesToDateStr = (updates: UpdateTimestamp) => {
        if (updates) {
            const {year, month, day, hours, minutes} = updates;
            const dateTime = new Date(year, month - 1, day, hours, minutes);
            return formatDate(dateTime, priority.netitems.form.ColumnType.Date14);
        }
        return '';
    };

    return (
        <Flex
            componentClasses={cx(styles.header, {
                [styles.noBorderBottom]: isChartDisplay
            })}
            {...createDataTestId('reports-header', true)}
        >
            {isChartDisplay && (
                <Flex alignItems="flex-start">
                    {chartHeader && currentPage === Pages.chartDisplay && (
                        <Flex
                            direction="column"
                            alignItems="flex-start"
                            componentClasses={styles.chartHeader}
                        >
                            <Text>{`${chartHeader.Company}, ${chartHeader.User}`}</Text>
                            <Text componentClasses={'forceLtr'}>
                                {updatesToDateStr(chartHeader.Updated)}
                            </Text>
                            {chartHeader.Inputs.length > 0 && (
                                <HeaderInputs inputs={chartHeader.Inputs} />
                            )}
                        </Flex>
                    )}
                    <ButtonWithTooltip
                        tooltip={texts.exit}
                        skin="text"
                        onClick={handleClose}
                        prefixIcon={PriorityIcon.closeBig}
                    />
                </Flex>
            )}
            {!isChartDisplay && (
                <>
                    {isPredictiveModel && !isMain && (
                        <span className={styles.titleIcon}>
                            <AIStarIcon />
                        </span>
                    )}
                    <Title
                        componentClasses={styles.headerTitle}
                        textColor="primary-700"
                        skin="h3"
                        text={title}
                        {...createDataTestId('reports-header-title', true)}
                    />
                    <Flex componentClasses={styles.marginH} justifyContent="flex-end">
                        {!isMain && (
                            <>
                                <ButtonWithTooltip
                                    tooltip={shouldShowSaveTooltip && getSaveButtonTooltip()}
                                    disabled={isChart ? isSaveChartButtonDisabled : isSaveDisabled}
                                    onClick={onSaveReport}
                                    width="regularFixed"
                                >
                                    {texts.save}
                                </ButtonWithTooltip>
                                <Button
                                    disabled={getIsDisabled(isSaveChartButtonDisabled)}
                                    onClick={handleRunReport}
                                    skin="secondary"
                                    width="regularFixed"
                                >
                                    {texts.runReport}
                                </Button>
                            </>
                        )}
                        <>
                            <Button skin="text" onClick={onHelp} prefixIcon={PriorityIcon.help}>
                                {helpTexts.help}
                            </Button>
                            <Separator align="horizontal" height={30} />
                        </>
                        <ButtonWithTooltip
                            tooltip={texts.exit}
                            skin="text"
                            onClick={handleClose}
                            prefixIcon={PriorityIcon.closeBig}
                        />
                    </Flex>
                </>
            )}
            <ScreenLocker showLoader={isLoading} locked={isLoading} />
        </Flex>
    );
});

export default Header;
